import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import FieldDropdownV2 from 'src/components/FormComponents/FieldDropdownV2'
import FieldV2 from 'src/components/FormComponents/FieldV2'
import ReceivementUnitForm from 'src/features/receivement/components/ReceivementUnitForm'
import ReceivementWeightForm from 'src/features/receivement/components/ReceivementWeightForm'
import { actions } from 'src/features/state'
import { useLabelPrinting } from 'src/hooks/useLabelPrinting'
import { findDescription } from 'src/services/product/productService'
import { createBatch } from 'src/services/receivement/receivementService'
import { formatNumberBR } from 'src/utils'
import { batchMapper } from 'src/utils/mappers/batchMapper'
import Submit from 'src/components/FormComponents/Submit'
import { useRebeepState } from '../../hooks/useRebeepState'
import ModalPleaseRebeep from '../ModalPleaseRebeep'
import ModalWriteDescription from '../ModalWriteDescription'
import { getSchema } from './NewBatchForm.schema'
import LabelForDueDateField from 'src/features/receivement/components/LabelForDueDateField'
import ModalAlert from 'src/components/ModalAlert'
import { Typography } from '@mui/material'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import * as S from './NewBatchForm.styles.js'

const typeUnits = [
  { label: 'KG', value: 'KG' },
  { label: 'UN', value: 'UN' },
]

const NewBatchForm = ({
  setTableData,
  tiposAvaria,
  receivementId,
  operatorBadge,
  operatorName,
}) => {
  const [dueDateTypeConfig, setDueDateTypeConfig] = useState('REGULAR')
  const [isFrozen, setIsFrozen] = useState(false)

  const schema = useMemo(() => (
    getSchema(dueDateTypeConfig !== 'INDETERMINATE', isFrozen)
  ), [dueDateTypeConfig, isFrozen])

  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    resetField,
    watch,
    setFocus,
    formState: { isSubmitting },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      tipoAvarias: 'NENHUM',
      quantidadeAvarias: '0',
      unidade: 'UN',
      temperaturaItem: '',
    },
  })

  const dispatch = useDispatch()
  const [modelId, setModelId] = useState(null)
  const { rebeepState, changeRebeepState } = useRebeepState()
  const [packQtyReadOnly, setPackQtyReadOnly] = useState(false)

  const qtdCaixasWatch = watch('qtdCaixas', 0)
  const caixariaWatch = watch('caixaria', 0)
  const avulsoWatch = watch('avulso', 0)
  const avariaWatch = watch('quantidadeAvarias', 0)
  const totalWatch = watch('total', 0)
  const weightWatch = watch('weight', 0.000)

  const measureUnit = watch('unidade')

  const dueDate = watch('validade')
  const [deliveryDaysMinExpirationDate, setDeliveryDaysMinExpirationDate] = useState(0)
  const [dueDateType, setDueDateType] = useState(0)

  const dueDateTypeInfo = useMemo(() => {
    switch (dueDateType) {
      case 1:
        return {
          title: 'Atenção: Validade próxima!',
          alertIconColor: '#FFC400',
          message: 'Atenção: Esse lote está próximo da validade!',
          fieldLabel: <LabelForDueDateField showAlert alertColor='#FFC400' tooltipMessage='Atenção: Validade baixa' />,
          damage: false
        }
      case 2:
        return {
          title: 'Atenção: Produto vencido!',
          alertIconColor: '#FF0000',
          message: 'Atenção: Esse lote está vencido!',
          fieldLabel: <LabelForDueDateField showAlert alertColor='#FF0000' tooltipMessage='Atenção: Produto vencido' />,
          damage: true
        }
      default:
        return {
          title: '',
          alertIconColor: '#000',
          message: '',
          fieldLabel: <LabelForDueDateField showAlert={false} />,
          damage: false
        }
    }
  }, [dueDateType])

  const resetDueDateAndDamageFields = (newDueDateType) => {
    if (dueDateType === 2 && newDueDateType !== 2) {
      setValue('tipoAvarias', 'NENHUM')
      setValue('quantidadeAvarias', '0')
    }
    setDueDateType(newDueDateType)
  }

  const [showModalAlertDueDate, setShowModalAlertDueDate] = useState(false)

  useEffect(() => {
    if (dueDate) {
      const inputDueDate = new Date(dueDate)
      inputDueDate.setHours(inputDueDate.getHours() + (inputDueDate.getTimezoneOffset() / 60))

      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)

      if (inputDueDate < currentDate) {
        setDueDateType(2)
        setValue('tipoAvarias', 'VENCIDO')
        setValue('quantidadeAvarias', measureUnit === 'KG' ? weightWatch : totalWatch)
        return
      }

      if (inputDueDate <= currentDate.setDate(currentDate.getDate() + deliveryDaysMinExpirationDate)) {
        resetDueDateAndDamageFields(1)
        return
      }

      resetDueDateAndDamageFields(0)
      return
    }

    resetDueDateAndDamageFields(0)
  }, [dueDate])

  const evaluateRebeepState = (barcode) => {
    const newState = barcode !== rebeepState.lastBarcode
      ? 'SHOWING_MODAL_PLEASE_REBEEP'
      : 'SHOWING_MODAL_WRITE_DESCRIPTION'

    changeRebeepState(newState, barcode)

    return newState
  }

  const resetFields = () => {
    resetField('qtdCaixas')
    resetField('caixaria')
    resetField('avulso')
    resetField('total')
    resetField('validade')
    resetField('tipoAvarias')
    resetField('quantidadeAvarias')
    resetField('weight')
    resetField('temperaturaItem')
  }

  const barcodeFilled = async () => {
    const barcode = getValues('codigoDeBarras')

    if (!barcode) {
      setValue('descricao', '')
      changeRebeepState('INITIAL')
      resetFields()
      return
    }

    try {
      dispatch(actions.isLoading(true))
      const {
        desc_produto,
        id_modelo,
        measure_unit,
        due_date_type,
        delivery_days_min_expiration_date,
        barcode_pack_qty,
        barcode_type,
        is_frozen
      } = await findDescription(barcode, receivementId)

      setIsFrozen(is_frozen || false)

      if (id_modelo === 'NAO CADASTRADO') {
        evaluateRebeepState(barcode)
        setValue('descricao', '')
        setValue('unidade', 'UN')
        setModelId(null)
        resetFields()
        return
      }

      changeRebeepState('INITIAL')
      setValue('descricao', desc_produto)
      setModelId(id_modelo)
      setDueDateTypeConfig(due_date_type)
      setDeliveryDaysMinExpirationDate(delivery_days_min_expiration_date)
      resetFields()

      if (measure_unit?.is_kg) {
        setValue('unidade', 'KG')
        if (is_frozen) {
          setFocus('temperaturaItem')
        } else {
          setFocus('weight')
        }
      } else {
        setValue('unidade', 'UN')
        if (is_frozen) {
          setFocus('temperaturaItem')
        } else {
          setFocus('qtdCaixas')
        }
        if (barcode_type === 'EAN') {
          setPackQtyReadOnly(false)
        } else if (barcode_type === 'DUN') {
          setValue('caixaria', barcode_pack_qty)
          setPackQtyReadOnly(true)
        }
      }
    } catch (e) {
      console.error(e)
      setValue('descricao', '')
      setValue('codigoDeBarras', '')
      setModelId(null)
      setFocus('codigoDeBarras')
      changeRebeepState('INITIAL')
      toast.error('Problema ao buscar descrição')
    } finally {
      dispatch(actions.isLoading(false))
    }
  }

  const handleCloseModalRebeep = () => {
    const lastBarcode = getValues('codigoDeBarras')
    setValue('codigoDeBarras', '')
    changeRebeepState('WAITING_SECOND_BEEP', lastBarcode)
    setTimeout(() => setFocus('codigoDeBarras'), 400)
  }

  const handleCloseWriteDescription = () => {
    changeRebeepState('WAITING_DESCRIPTION')
    setTimeout(() => setFocus('descricao'), 400)
  }

  const { showModalLabelPrinting, labelPrintingComponent } = useLabelPrinting({
    operatorName,
    onFinish: () => {
      reset()
      changeRebeepState('INITIAL')
      setTimeout(() => setFocus('codigoDeBarras'), 400)
    },
  })

  const onSubmit = async (batch) => {
    if (dueDateType !== 0 && !showModalAlertDueDate) {
      setShowModalAlertDueDate(true)
      return
    }

    try {
      const responseBatch = await createBatch(
        operatorBadge,
        receivementId,
        modelId,
        batch,
      )

      const newTableData = []
      
      for (const batch of responseBatch) {
        const newBatch = batchMapper(batch, measureUnit)
        newTableData.push(newBatch)
      }

      setTableData((tableData) => [...newTableData, ...tableData])
      showModalLabelPrinting(newTableData)
      setIsFrozen(false)
      reset()
      setDueDateTypeConfig('REGULAR')
      setDueDateType(0)
    } catch (err) {
      toast.error(err?.response?.data?.message)
    } finally {
      setShowModalAlertDueDate(false)
    }
  }

  const enterKeyCode = 13
  const tabKeyCode = 9

  const handleOnKeyDown = (event, nextField, customAction) => {
    if ([enterKeyCode, tabKeyCode].includes(event.keyCode) && !event.shiftKey) {
      event.preventDefault()
      document.getElementById(nextField).focus()
      if (customAction !== undefined) {
        customAction()
      }
    }
  }

  const parseIntOrZero = (value) => {
    const newValue = parseInt(value, 10)
    return Number.isNaN(newValue) ? 0 : newValue
  }

  useEffect(() => {
    if (measureUnit === 'KG' && dueDateType === 2) {
      setValue('quantidadeAvarias', weightWatch)
    } else if (measureUnit === 'UN') {
      const total = (parseIntOrZero(qtdCaixasWatch) * parseIntOrZero(caixariaWatch)) + parseIntOrZero(avulsoWatch)

      setValue('total', formatNumberBR(total))

      if (dueDateType === 2) {
        setValue('quantidadeAvarias', totalWatch)
      }
    }
  }, [qtdCaixasWatch, caixariaWatch, avulsoWatch, weightWatch, setValue])

  useEffect(() => {
    const qtdUtil = parseIntOrZero(totalWatch) - parseIntOrZero(avariaWatch)

    setValue(
      'qtdUtil', formatNumberBR(qtdUtil)
    )
  }, [totalWatch, avariaWatch, setValue])

  useEffect(() => {
    let qtdUtilKg = 0

    if ((typeof weightWatch === 'string') && (typeof avariaWatch === 'string')) {
      qtdUtilKg = parseFloat(weightWatch.replace(',', '.')) - parseFloat(avariaWatch.replace(',', '.'))
    } else if ((typeof weightWatch === 'string') && (typeof avariaWatch === 'number')) {
      qtdUtilKg = parseFloat(weightWatch.replace(',', '.')) - avariaWatch
    }

    setValue(
      'qtdUtilKg', formatNumberBR(qtdUtilKg, 3)
    )
  }, [weightWatch, avariaWatch, setValue])

  return (
    <>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <S.Fieldset>
          <S.Field
            as={FieldV2}
            control={control}
            name='codigoDeBarras'
            labelText='Código'
            type='text'
            autoFocus
            placeholder='Bipar'
            onKeyDown={(e) => handleOnKeyDown(e, 'descricao', barcodeFilled)}
            autoComplete='off'
          />
          <S.FieldDescription
            as={FieldV2}
            control={control}
            name='descricao'
            labelText='Descrição'
            type='text'
            readOnly={!rebeepState.descriptionIsWritable}
            onKeyDown={(e) => handleOnKeyDown(e, 'unidade')}
            autoComplete='off'
          />
          <S.Field
            as={FieldDropdownV2}
            control={control}
            name='unidade'
            labelText='Unidade'
            type='text'
            options={typeUnits}
            disabled={!rebeepState.descriptionIsWritable}
            onKeyDown={(e) => handleOnKeyDown(e, isFrozen ? 'temperaturaItem' : (measureUnit === 'KG' ? 'weight' : 'qtdCaixas'))}
            onChange={resetFields}
          />
          {isFrozen && (
            <S.Field
              as={FieldV2}
              control={control}
              name='temperaturaItem'
              labelText='Temperatura (°C) *'
              type='number'
              disabled={isSubmitting}
              onKeyDown={(e) => handleOnKeyDown(e, measureUnit === 'KG' ? 'weight' : 'qtdCaixas')}
              inputProps={{
                min: '-200',
                max: '200',
                step: '0.1',
                required: true,
                id: 'temperaturaItem',
              }}
              required
            />
          )}
        </S.Fieldset>
        <S.Fieldset>
          {measureUnit === 'KG' ? (
            <ReceivementWeightForm
              tiposAvaria={tiposAvaria}
              watch={watch}
              control={control}
              handleOnKeyDown={handleOnKeyDown}
              setValue={setValue}
              resetField={resetField}
              dueDateTypeConfig={dueDateTypeConfig}
              dueDateDamage={dueDateTypeInfo.damage}
              labelForDueDate={dueDateTypeInfo.fieldLabel}
            />
          ) : (
            <ReceivementUnitForm
              tiposAvaria={tiposAvaria}
              watch={watch}
              control={control}
              handleOnKeyDown={handleOnKeyDown}
              setValue={setValue}
              resetField={resetField}
              dueDateTypeConfig={dueDateTypeConfig}
              dueDateDamage={dueDateTypeInfo.damage}
              labelForDueDate={dueDateTypeInfo.fieldLabel}
              packQtyReadOnly={packQtyReadOnly}
            />
          )}
          <S.ButtonWrapper>
            <Submit
              text='Incluir'
              height='50px'
              width='200px'
              disabled={isSubmitting}
            />
          </S.ButtonWrapper>
        </S.Fieldset>
      </S.Form>

      <ModalWriteDescription
        show={rebeepState.isModalWriteDescriptionOpen}
        action={handleCloseWriteDescription}
      />

      <ModalPleaseRebeep
        show={rebeepState.isModalRebeepOpen}
        action={handleCloseModalRebeep}
      />

      {dueDateType !== 0 && (
        <ModalAlert
          show={showModalAlertDueDate}
          title={dueDateTypeInfo.title}
          centered
          action={handleSubmit(onSubmit)}
          okButtonDisabled={isSubmitting}
          closeButtonAction={() => setShowModalAlertDueDate(false)}
          closeButtonDisabled={isSubmitting}
          customizableBody={
            <S.ModalAlertDueDateContainer>
              <ReportProblemIcon
                sx={{
                  fontSize: 80,
                  color: dueDateTypeInfo.alertIconColor
                }}
                data-testid='modal-alert-icon-test-id'
              />
              <S.ModalAlertDueDateInfo>
                <Typography variant='body1' color='textPrimary' sx={{ fontWeight: 600 }}>
                  {dueDateTypeInfo.message}
                </Typography>
                <Typography variant='body1' color='textPrimary' sx={{ fontWeight: 600 }}>
                  Guarde-o separadamente pois ele poderá ser devolvido pelo time de compras!
                </Typography>
              </S.ModalAlertDueDateInfo>
            </S.ModalAlertDueDateContainer>
          }
          testId='modal-due-date-alert-test-id'
        />
      )}

      {labelPrintingComponent}
    </>
  )
}

export default NewBatchForm
